import React, { useState, useEffect } from 'react';
import { battlesApi } from '../../api/battles';
import '../../css/components/RoundsTabs.css';

/**
 * Прогрессбар раунда.
 *
 * Показывает две полосы:
 * 1. Прогресс времени раунда (startTime → endTime) — сколько процентов
 *    длительности раунда уже прошло. Для ENDED — 100%, для будущих — 0%.
 * 2. Прогресс загрузки треков — количество берётся с сервера
 *    (GET /battles/{battleId}/rounds/{roundId}/tracks/count),
 *    а не подсчётом загруженного списка треков.
 *
 * @component
 * @param {Object} props.round - Данные раунда (id, startTime, endTime, status)
 * @param {string} props.battleId - UUID баттла (для запроса количества треков)
 * @param {number} [props.participantsCount=0] - Ожидаемое число участников
 * @param {boolean} [props.showTracks=false] - Показывать полосу треков
 */
const RoundProgressBar = ({
  round,
  battleId,
  participantsCount = 0,
  showTracks = false
}) => {
  const [tracksCount, setTracksCount] = useState(null);

  // Загружаем количество сданных треков с сервера
  useEffect(() => {
    if (!showTracks || !battleId || !round?.id) {
      return undefined;
    }

    let cancelled = false;

    battlesApi
      .getRoundTracksCount(battleId, round.id)
      .then((count) => {
        if (!cancelled) setTracksCount(count);
      })
      .catch((err) => {
        console.error('[RoundProgressBar] Failed to load tracks count:', err.message);
        if (!cancelled) setTracksCount(null);
      });

    return () => {
      cancelled = true;
    };
  }, [showTracks, battleId, round?.id, round?.status]);

  const now = Date.now();
  const start = round?.startTime ? new Date(round.startTime).getTime() : null;
  const end = round?.endTime ? new Date(round.endTime).getTime() : null;

  // Прогресс времени раунда
  let timePercent = null;
  if (start && end && end > start) {
    if (round?.status === 'ENDED' || now >= end) {
      timePercent = 100;
    } else if (now <= start) {
      timePercent = 0;
    } else {
      timePercent = Math.min(100, Math.round(((now - start) / (end - start)) * 100));
    }
  } else if (round?.status === 'ENDED') {
    timePercent = 100;
  }

  // Прогресс загрузки треков (количество с сервера)
  const uploaded = tracksCount;
  const expected = participantsCount > 0 ? participantsCount : 0;
  const trackPercent =
    showTracks && uploaded !== null && expected > 0
      ? Math.min(100, Math.round((uploaded / expected) * 100))
      : null;

  if (timePercent === null && trackPercent === null) {
    return null;
  }

  return (
    <div className="round-progress">
      {timePercent !== null && (
        <div className="round-progress__row">
          <div className="round-progress__bar" role="progressbar" aria-valuenow={timePercent} aria-valuemin="0" aria-valuemax="100">
            <div
              className={`round-progress__fill round-progress__fill--time ${
                timePercent >= 100 ? 'round-progress__fill--done' : ''
              }`}
              style={{ width: `${timePercent}%` }}
            />
          </div>
          <span className="round-progress__label">
            {timePercent >= 100 ? 'Раунд завершён' : `⏳ ${timePercent}% времени раунда`}
          </span>
        </div>
      )}

      {trackPercent !== null && (
        <div className="round-progress__row">
          <div className="round-progress__bar" role="progressbar" aria-valuenow={trackPercent} aria-valuemin="0" aria-valuemax="100">
            <div
              className={`round-progress__fill round-progress__fill--tracks ${
                trackPercent >= 100 ? 'round-progress__fill--done' : ''
              }`}
              style={{ width: `${trackPercent}%` }}
            />
          </div>
          <span className="round-progress__label">
            🎤 Треки: {uploaded} из {expected}
          </span>
        </div>
      )}
    </div>
  );
};

export default RoundProgressBar;
